// Firebase Cloud Messaging — HANDOFF schema Section 8.
// Web Push only works for installed (home-screen) PWAs on iOS 16.4+; in an
// ordinary Safari tab it silently does nothing, which callers should expect.

import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';
import { registerPush } from '../worker/api';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

let appInstance = null;

function getApp() {
  if (!appInstance) appInstance = initializeApp(firebaseConfig);
  return appInstance;
}

/**
 * Requests notification permission and returns an FCM token, or null if
 * permission was denied or the platform doesn't support Web Push
 * (e.g. iOS Safari not installed to the home screen).
 *
 * IMPORTANT: explicitly registers firebase-messaging-sw.js at its own
 * dedicated scope, rather than using navigator.serviceWorker.ready (which
 * would resolve to the app's OWN Workbox-generated sw.js -- a completely
 * different service worker with no idea how to handle a push event). This
 * was the actual bug behind notifications never arriving at all: getToken()
 * was creating a push subscription tied to the wrong service worker, one
 * with no Firebase messaging code in it, so incoming pushes landed with
 * nothing to display them -- regardless of foreground/background state,
 * which is why neither ever worked.
 */
export async function requestNotificationPermission() {
  if (!(await isSupported().catch(() => false))) return null;
  if (typeof Notification === 'undefined') return null;

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return null;

  const swRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
    scope: '/firebase-cloud-messaging-push-scope',
  });
  const messaging = getMessaging(getApp());

  try {
    const token = await getToken(messaging, {
      vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY,
      serviceWorkerRegistration: swRegistration,
    });
    return token || null;
  } catch (err) {
    console.error('FCM getToken failed', err);
    return null;
  }
}

/**
 * Handles a push that arrives while the PWA is actually open/foregrounded.
 * This is a real gap the app had until now: firebase-messaging-sw.js's
 * onBackgroundMessage only fires when the app is closed/backgrounded --
 * when Firebase detects a focused, controlling client, it hands the
 * payload to the page instead of showing a notification itself, and
 * WITHOUT this handler that payload just went nowhere. Call this once,
 * e.g. from App.jsx on mount.
 */
export async function listenForForegroundMessages() {
  if (!(await isSupported().catch(() => false))) return;
  const messaging = getMessaging(getApp());
  onMessage(messaging, (payload) => {
    const { title, body } = payload.notification ?? {};
    if (Notification.permission === 'granted') {
      new Notification(title ?? 'ASPIRE Study', {
        body: body ?? 'Time for your study session.',
        icon: '/icons/icon-192.png',
      });
    }
  });
}

/**
 * Re-claims push registration for THIS device on every app launch, if
 * permission is already granted.
 *
 * The server stores exactly one FCM token per participant, so whichever device
 * registered most recently receives the reminders. Without this, registration
 * only ever happened during schedule setup — meaning a participant who
 * reinstalled the PWA, cleared their site data, or simply had their token
 * rotated by the browser would stop receiving notifications permanently, with
 * nothing anywhere reporting a problem. It also means the phone actually being
 * used is the one that gets notified when a participant ID has been set up on
 * more than one.
 *
 * Deliberately does NOT prompt: it returns early unless permission was already
 * granted, so opening the app can never trigger a permission dialog out of
 * nowhere. Failures are swallowed — this is best-effort background upkeep and
 * must never block the app from starting.
 */
export async function refreshPushRegistration({ participantId, schedule, currentToken, onToken }) {
  try {
    if (!participantId || !schedule) return;
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    if (!(await isSupported().catch(() => false))) return;

    const token = await requestNotificationPermission();
    if (!token) return;

    await registerPush({ participantId, fcmToken: token, schedule });
    if (token !== currentToken) await onToken?.(token);
  } catch (err) {
    console.warn('push re-registration failed', err);
  }
}

export function isNotificationPermissionGranted() {
  return typeof Notification !== 'undefined' && Notification.permission === 'granted';
}
